import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Component, Input, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import {
  CreateVoiceMessageByVocalistCommand,
  CustomResult,
  GetVoiceMessageGroupsWithPaginationDto,
} from 'src/app/core/api/models';
import { EventTypes } from 'src/app/core/constants/event-types';
import { FormPageConfig } from 'src/app/core/models/form-page-config.model';
import { AuthTokenService } from 'src/app/core/services/authentication/auth-token.service';
import { AppConfigService } from 'src/app/core/services/config.service';
import {
  DialogService,
  DialogStatus,
} from 'src/app/core/services/dialog.service';
import { VoiceMessagessendingMethod } from 'src/app/core/types/app-voice-messages-sending-method.type';
import { BaseFormPageComponent } from 'src/app/pages/shared/base/components/base-form-page.component';
import { VoiceMessagesService } from './../../../../core/api/services/voice-messages.service';
import { BaseFormModule } from 'src/app/shared/components/base/base-component/base-form.module';
import { AudioControllerModule } from "../../../../shared/components/audio-controller/audio-controller.module";
import { TextToVoiceConvertorComponent } from "../text-to-voice-convertor/text-to-voice-convertor.component";

@Component({
    selector: 'app-voice-messages-form',
    templateUrl: './voice-messages-form.component.html',
    styleUrls: ['./voice-messages-form.component.scss'],
    imports: [BaseFormModule, AudioControllerModule, TextToVoiceConvertorComponent]
})
export class VoiceMessagesFormComponent
  extends BaseFormPageComponent
  implements OnInit
{
  //#region properties
  @Input() voiceGroup: GetVoiceMessageGroupsWithPaginationDto;
  formPageConfig: FormPageConfig<any> = {};
  model: {
    File?: Blob | null;
    Name?: string | null;
    AgentVoiceMessageId?: number | null;
  } = {};

  protected currentService = inject(VoiceMessagesService);
  sendingMethod: VoiceMessagessendingMethod = 'uploadFile';
  agentVoiceMessageId: number;

  createAction: string =
    AppConfigService.config.apiUrl + '/VoiceMessages/UploadFile';

  voiceFileSrc: string;
  textToAudioConverterSoundPath: string;

  textToAudioForm: FormGroup;
  textToAudioFormId: string = 'textToVoiceConvertorFormId';

  //#endregion

  constructor(
    public dialogService: DialogService,
    private translate: TranslateService,
    public http: HttpClient
  ) {
    super();
  }

  async ngOnInit() {
    super.ngOnInit();
  }

  initForm(): void {
    this.form = new FormGroup({
      file: new FormControl('', Validators.required),
      sendingMethod: new FormControl(''),
      name: new FormControl('', Validators.required),
    });
  }

  //#region Api

  //Create
  baseCreateApi(model) {
    try {
      this.unSubs();
      this.loading = true;

      let formData: any = new FormData();
      let fileToUpload = <File>this.model.File;

      formData.append('Name', this.model.Name);
      formData.append(
        'File',
        fileToUpload,
        fileToUpload.name.replace(/\s/g, '')
      );
      formData.append('VoiceMessageGroupId', this.voiceGroup.id);

      if (this.model.AgentVoiceMessageId)
        formData.append('AgentVoiceMessageId', this.model.AgentVoiceMessageId);

      const headers = new HttpHeaders()
        .set('Accept', 'application/json')
        .set('enctype', 'multipart/form-data')
        .set('Authorization', 'Bearer ' + AuthTokenService.getAccessToken());

      this.http
        .post<CustomResult>(this.createAction, formData, { headers: headers })
        .pipe(this.basePipeOperators())
        .subscribe(this.baseObserver);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.voiceMessages
      );
    }
  }

  //Update
  baseUpdateApi(model) {
    this.unSubs();

    this.loading = true;

    model.id = this.formPageConfig.editId;

    this.currentService
      .voiceMessagesUpdateUploadedFilePost({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
    // this.formPageConfig.isEditMode = false;
  }

  CreateVoiceMessageByVocalistApi(model: CreateVoiceMessageByVocalistCommand) {
    this.unSubs();

    this.loading = true;

    this.currentService
      .voiceMessagesCreateVoiceMessageByVocalist({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
    // this.formPageConfig.isEditMode = false;
  }

  //#endregion

  onChangeSendingMethod(event) {
    try {
      this.form.get('file').clearValidators();
      if (this.sendingMethod == 'uploadFile')
        this.form.get('file').setValidators([Validators.required]);
      this.form.get('file').updateValueAndValidity();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.voiceMessages
      );
    }
  }

  getUploadedFile(file) {
    try {
      this.model.File = file;
      this.voiceFileSrc = URL.createObjectURL(file);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.voiceMessages
      );
    }
  }

  getFileFromTextToVoiceConvertor(blob) {
    try {
      this.model.File = blob;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.voiceMessages
      );
    }
  }

  onSubmit(): void {
    try {
      if (this.loading) return;
      if (!this.isValidForm(this.form, this.tagName)) return;

      if (this.sendingMethod == 'voiceReader') {
        if (
          !this.isValidForm(
            this.textToAudioForm,
            this.tagName,
            this.textToAudioFormId
          )
        )
          return;

        this.CreateVoiceMessageByVocalistApi({
          agentVoiceMessageId: this.model.AgentVoiceMessageId,
          name: this.model.Name,
          path: this.textToAudioConverterSoundPath,
          voiceMessageGroupId: this.voiceGroup.id,
        });
        return;
      }

      this.checkCreateOrUpdate();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.voiceMessages
      );
    }
  }

  isExistFilePatch() {
    try {
      if (!this.textToAudioConverterSoundPath) {
        const errMsg = this.translate.instant('messages.isExistFilePatch');
        this.dialogService.openDialog({
          title: 'common.error',
          message: errMsg,
          status: DialogStatus.error,
        });
        return false;
      }
      return true;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.voiceMessages
      );
    }
  }
}
